import { StorageManager } from './storageManager';
import { isStoredMessageArray, type StoredMessage } from '../types';

/**
 * Render a stored chat session as a Markdown transcript suitable for
 * download. Pulls the message log from localStorage (`chat_<id>`) so we
 * include grounding source + citations + thread summaries that the backend
 * doesn't echo back.
 */
export function exportConversationToMarkdown(sessionId: string, title: string): string {
  const messages = StorageManager.safeGetJSON(`chat_${sessionId}`, isStoredMessageArray) || [];
  const lines: string[] = [];

  lines.push(`# ${title || 'Conversation'}`);
  lines.push('');
  lines.push(`_Exported ${new Date().toLocaleString()}_`);
  lines.push('');
  lines.push('---');
  lines.push('');

  messages.forEach((msg: StoredMessage) => {
    const isUser = msg.isUser ?? msg.sender === 'user';
    const role = isUser ? 'User' : 'R&R Realty AI';
    const ts = (() => {
      try { return new Date(msg.timestamp).toLocaleString(); }
      catch { return String(msg.timestamp); }
    })();

    lines.push(`### ${role} — ${ts}`);
    lines.push('');
    lines.push(msg.content);
    lines.push('');

    if (msg.citations && msg.citations.length > 0) {
      lines.push('**Sources:**');
      msg.citations.forEach((c, i) => {
        const label = c.title || c.url;
        lines.push(`${i + 1}. [${label}](${c.url})`);
      });
      lines.push('');
    }

    if (msg.groundingSource) {
      lines.push(`_Grounded via: ${msg.groundingSource}_`);
      lines.push('');
    }
  });

  return lines.join('\n');
}

/** Trigger a browser download for the given text content. */
export function downloadTextFile(filename: string, content: string, mimeType = 'text/markdown;charset=utf-8'): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Convenience: build the markdown and trigger the download in one call. */
export function exportAndDownloadConversation(sessionId: string, title: string): void {
  const md = exportConversationToMarkdown(sessionId, title);
  // Sanitize the title for filesystem safety; cap to a reasonable length.
  const safeTitle = (title || 'conversation')
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'conversation';
  const date = new Date().toISOString().slice(0, 10);
  downloadTextFile(`${date}-${safeTitle}.md`, md);
}
